<template>
	<span>
		<Pupup @close="close" v-if="!singleVideo" :showClose="true" :title="title" :width="1200">
			<template v-slot:content>
				<p>
					Our platform has had a lot of updates recently so you will notice a few differences in the videos. They will be updated soon. The
					core functionality is the same though so the videos should still be helpful. Still having problems?
					<button type="button" class="contact-team-link" @click="openContactWidget">Contact the team</button>.
				</p>
				<div class="video-list">
					<h3 class="videos-title" v-if="videoList.current.length > 0 && videoList.other.length > 0">Related Videos</h3>

					<div
						class="video-item"
						v-for="(video, videoIndex) in videoList.current"
						:key="'video-current-' + videoIndex"
						@click="loadVideo(video.slug)">
						<div class="video-thumb"><img :src="video.thumb" :alt="video.title" /></div>
						<h2 class="video-title" v-if="showVideoTitle">{{ video.title }}</h2>
					</div>

					<h3 class="videos-title other" v-if="videoList.current.length > 0 && videoList.other.length > 0">Other Videos</h3>

					<div
						class="video-item"
						v-for="(video, videoIndex) in videoList.other"
						:key="'video-other-' + videoIndex"
						@click="loadVideo(video.slug)">
						<div class="video-thumb"><img :src="video.thumb" :alt="video.title" /></div>
						<h2 class="video-title" v-if="showVideoTitle">{{ video.title }}</h2>
					</div>
				</div>
			</template>
		</Pupup>

		<VideoPlayer
			v-if="showVideo"
			:videoId="videoId"
			:type="type"
			:title="theVideoTitle"
			@close="closeVideo"
			:customPlaylist="videoIds"
			:autoPlay="autoplayVideo" />
	</span>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import general from "./../utils/general";
import VideoPlayer from "./VideoPlayer.vue";
import Pupup from "./Display/Pupup.vue";

export default {
	name: "Videos",
	components: {
		VideoPlayer,
		Pupup,
	},
	props: {},
	emits: ["close"],
	setup(props, { emit }) {
		const vm = getCurrentInstance().proxy;
		const store = vm.$store;

		// --- State ---
		const videoId = ref(null);
		const showVideo = ref(false);
		const fullClose = ref(false);
		const type = ref("youtube");

		// --- Vuex State/Mutations ---
		const videos = computed(() => store.state.general.videos);
		const playVideo = computed(() => store.state.general.playVideo);
		const videoTitle = computed(() => store.state.general.videoTitle);
		const singleVideo = computed(() => store.state.general.singleVideo);
		const autoplayVideo = computed(() => store.state.general.autoplayVideo);

		const updateVar = (payload) => store.commit("general/updateVar", payload);

		// --- Computed ---
		const title = computed(() => "Video Guides");
		const showVideoTitle = computed(() => false);

		const theVideoTitle = computed(() => {
			if (videoTitle.value) return videoTitle.value;
			if (videoId.value) {
				const v = videos.value.find((vid) => vid.videoId === videoId.value);
				if (v) return v.title;
			}
			return "";
		});

		const videoList = computed(() => {
			let vids = [];
			let vidsThisPage = [];
			const page = vm.$route.name;

			videos.value.forEach((video) => {
				if (video.ignore) return;
				const thumb = `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`;
				const videoData = { slug: video.slug, title: video.title, thumb: thumb };

				if (video.pageNames.includes(page)) {
					vidsThisPage.push(videoData);
				} else {
					vids.push(videoData);
				}
			});

			return { other: vids, current: vidsThisPage };
		});

		const videoIds = computed(() => []);

		// --- Methods ---
		const loadVideo = (slug) => {
			const v = videos.value.find((vid) => vid.slug === slug);
			if (v) {
				videoId.value = v.videoId;
				type.value = v.type || "youtube";
				showVideo.value = true;
			}
		};

		const closeVideo = () => {
			showVideo.value = false;
			general.modalClose();
			if (fullClose.value) {
				close();
			}
		};

		const close = () => {
			emit("close");
		};

		const openContactWidget = () => {
			store.dispatch("feedback/openWidget", {
				type: "contact",
			});
		};

		// --- Lifecycle Hooks ---
		onMounted(() => {
			if (playVideo.value) {
				loadVideo(playVideo.value);
				fullClose.value = true;
			}
			updateVar({ varName: "playVideo", info: null });
		});

		return {
			videoId,
			showVideo,
			type,
			singleVideo,
			autoplayVideo,
			title,
			showVideoTitle,
			theVideoTitle,
			videoList,
			videoIds,
			closeVideo,
			close,
			loadVideo,
			openContactWidget,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../assets/css/less/_helper.less";
@import "./../assets/css/less/_settings.less";
.modal {
	.modal-wrap {
		&.hidden {
			.opacity(0);
		}
	}
}
.video-list {
	.videos-title {
		color: @guidedRose;
		margin-top: 20px;
	}

	.video-item {
		cursor: pointer;
		vertical-align: top;

		.video-title {
			margin-top: 10px;
			font-size: 20px;
		}

		.video-thumb {
			position: relative;
			cursor: pointer;
			margin-top: 20px;
			overflow: hidden;

			&:last-child {
				margin-top: 0;
			}

			img {
				max-width: 100%;
				position: relative;
			}
		}

		&:hover {
			.video-thumb {
				img {
					.opacity(0.8);
				}

				&:after {
					.opacity(1);
				}
			}
		}
	}
}

.contact-team-link {
	padding: 0;
	border: none;
	background: transparent;
	color: @textLinkTeal;
	font: inherit;
	text-decoration: underline;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		color: @formDefaultColour;
	}
}

@media screen and (min-width: 540px) {
	.video-list {
		.video-item {
			display: inline-block;
			margin-bottom: 20px;
			width: 100%;
			text-align: left;

			.video-thumb {
				display: block;
				box-sizing: border-box;
				margin-top: 0;
			}

			.video-title {
				display: block;
				text-align: center;
				box-sizing: border-box;
			}
		}
	}
}

@media screen and (min-width: 768px) {
	.video-list {
		.video-item {
			width: 31%;
			margin-left: 2%;
		}
	}
}
</style>
